import React from 'react';
import moment from 'moment';
import { readDate, writeDate } from '../../utils/appDate';
import { T, FONT_STACK } from '../customer/customers.theme';
import {
  Dialog,
  DialogHeader,
  ErrorLine,
  PlusIcon,
  PrimaryButton,
} from '../customer/CustomersUI';
import { COPY } from './expenses.copy';

/**
 * Adding an expense, and editing one.
 *
 * One dialog for both, as on the phone — the fields are identical, so a field
 * added later cannot be forgotten on the other form.
 *
 * The order of the fields is the phone's: amount first, because that is the
 * number the person is holding a receipt for, then what it was, then when,
 * then which category, then how it was paid.
 */

/** The phone's two, in its own lower case. */
const PAYMENT_TYPES = [COPY.cash, COPY.invoice];

const labelStyle = {
  fontSize: 12,
  fontWeight: 600,
  color: T.inkSoft,
  paddingBottom: 6,
};

const fieldStyle = (disabled) => ({
  width: '100%',
  height: 44,
  padding: '0 14px',
  background: disabled ? T.sunk : T.surface,
  border: `1px solid ${T.borderStrong}`,
  borderRadius: T.radiusControl,
  font: 'inherit',
  fontFamily: FONT_STACK,
  fontSize: 14,
  color: T.ink,
  outline: 'none',
  boxSizing: 'border-box',
});

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <span style={labelStyle}>{label}</span>
      {children}
    </div>
  );
}

export default function ExpenseDialog({
  expense,
  categories,
  vendors,
  vendorsLoading,
  onNeedVendors,
  onManageCategories,
  onClose,
  onSubmit,
}) {
  const editing = Boolean(expense);

  const [amount, setAmount] = React.useState(() =>
    expense ? String(expense.amount ?? '') : ''
  );
  const [description, setDescription] = React.useState(
    () => (expense && expense.description) || ''
  );
  const [notes, setNotes] = React.useState(() => (expense && expense.notes) || '');
  // The stored day as the shop meant it, not as this browser's offset reads it.
  const [date, setDate] = React.useState(() =>
    expense ? readDate(expense.date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')
  );

  /**
   * The time of day, which the picker never shows.
   *
   * The field is a date only, but the list prints a time beside every row and
   * two expenses on the same day have to sort. So the clock is carried
   * alongside: the original time when editing, the time the form was opened
   * when adding. Midnight for everything would collapse a day into one
   * unordered heap.
   */
  const timePart = React.useRef(
    (expense ? readDate(expense.date) : moment()).format('HH:mm:ss.SSS')
  );
  const [paymentType, setPaymentType] = React.useState(
    () => (expense && expense.paymentType) || COPY.cash
  );

  const [categoryId, setCategoryId] = React.useState(() => {
    if (!expense) return '';
    const type = expense.expenseType;
    if (!type) return '';
    return typeof type === 'object' ? String(type._id || '') : String(type);
  });

  const [vendorId, setVendorId] = React.useState(() => {
    if (!expense) return '';
    const vendor = expense.vendor;
    if (!vendor) return '';
    return typeof vendor === 'object' ? String(vendor._id || '') : String(vendor);
  });

  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  const isInvoice = paymentType === COPY.invoice;

  /**
   * The first category is selected for you, the way the phone's dropdown does
   * it — an expense almost always belongs to one, and picking the common case
   * saves a tap on every single entry.
   */
  React.useEffect(() => {
    if (categoryId || !categories.length) return;
    setCategoryId(String(categories[0]._id));
  }, [categories, categoryId]);

  /**
   * A category deleted while this was open would leave the select pointing at
   * nothing, which submits an empty expenseType and files the expense under
   * the catch-all.
   */
  React.useEffect(() => {
    if (!categoryId) return;
    if (categories.some((row) => String(row._id) === categoryId)) return;
    setCategoryId(categories.length ? String(categories[0]._id) : '');
  }, [categories, categoryId]);

  /** Vendors cost a request, so they are fetched only once Invoice is chosen. */
  React.useEffect(() => {
    if (isInvoice && onNeedVendors) onNeedVendors();
  }, [isInvoice, onNeedVendors]);

  const submit = async () => {
    if (busy) return;

    // The phone's checks, in the phone's order, so the same mistake produces
    // the same sentence on both clients.
    const amountText = String(amount).trim();
    const descriptionText = description.trim();

    if (!amountText) return setError(COPY.amountRequired);
    if (!descriptionText) return setError(COPY.descriptionRequired);
    if (isInvoice && !vendorId) return setError(COPY.vendorRequired);

    const value = Number(amountText);
    if (!Number.isFinite(value) || value < 0) return setError(COPY.amountInvalid);

    if (!categoryId) return setError(COPY.categoryRequired);

    setBusy(true);
    setError('');

    try {
      await onSubmit({
        expense,
        categoryId,
        payload: {
          description: descriptionText,
          amount: value,
          paymentType,
          expenseType: categoryId,
          /**
           * Naive local wall-clock text, which is what every client in this
           * system sends and what the server expects to misread as UTC.
           *
           * This used to send `toISOString()`. That is a real instant, and
           * the server reads it as wall clock — so an expense entered at
           * midnight on the 15th was stored as the 14th and read back a day
           * early. See utils/appDate for the rule.
           */
          date: writeDate(`${date}T${timePart.current}`),
          notes: notes.trim(),
          // Sent only for an invoice. The server ignores it for cash, and an
          // id left behind by a switch back to cash must not reach a vendor.
          vendor: isInvoice ? vendorId : null,
        },
      });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || COPY.saveFailed);
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose} locked={busy} width={520}>
      <div style={{ padding: '22px 24px 24px' }}>
        <DialogHeader
          title={editing ? COPY.editTitle : COPY.addTitle}
          onClose={onClose}
          locked={busy}
        />

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 14,
            paddingTop: 18,
          }}
        >
          <Field label={COPY.amount}>
            <input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              disabled={busy}
              autoFocus
              onChange={(event) => {
                setAmount(event.target.value);
                setError('');
              }}
              style={fieldStyle(busy)}
            />
          </Field>

          <Field label={COPY.date}>
            <input
              type="date"
              value={date}
              disabled={busy}
              onChange={(event) => setDate(event.target.value)}
              style={fieldStyle(busy)}
            />
          </Field>

          <div style={{ gridColumn: '1 / -1' }}>
            <Field label={COPY.description}>
              <input
                value={description}
                disabled={busy}
                onChange={(event) => {
                  setDescription(event.target.value);
                  setError('');
                }}
                style={fieldStyle(busy)}
              />
            </Field>
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <Field label={COPY.category}>
              <div style={{ display: 'flex', gap: 10 }}>
                <select
                  value={categoryId}
                  disabled={busy}
                  onChange={(event) => {
                    setCategoryId(event.target.value);
                    setError('');
                  }}
                  style={{ ...fieldStyle(busy), flex: 1, cursor: 'pointer' }}
                >
                  {categories.length ? null : <option value="">{COPY.noCategories}</option>}
                  {categories.map((row) => (
                    <option key={row._id} value={row._id}>
                      {row.name}
                    </option>
                  ))}
                </select>

                {/* The phone puts a square + beside the dropdown for exactly
                    this: filing an expense under a category that does not
                    exist yet, without leaving the form. */}
                <button
                  type="button"
                  title={COPY.categoriesTitle}
                  aria-label={COPY.categoriesTitle}
                  onClick={onManageCategories}
                  disabled={busy}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 44,
                    height: 44,
                    flexShrink: 0,
                    background: T.ink,
                    border: 'none',
                    borderRadius: T.radiusControl,
                    color: T.surface,
                    cursor: busy ? 'not-allowed' : 'pointer',
                  }}
                >
                  <PlusIcon size={16} />
                </button>
              </div>
            </Field>
          </div>

          <Field label={COPY.paymentType}>
            <div style={{ display: 'flex', gap: 8 }}>
              {PAYMENT_TYPES.map((type) => {
                const active = paymentType === type;
                return (
                  <button
                    key={type}
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setPaymentType(type);
                      setError('');
                    }}
                    style={{
                      flex: 1,
                      height: 44,
                      background: active ? T.ink : T.shade,
                      border: 'none',
                      borderRadius: T.radiusControl,
                      font: 'inherit',
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: active ? T.surface : T.ink,
                      cursor: busy ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {type}
                  </button>
                );
              })}
            </div>
          </Field>

          {/* Dimmed rather than hidden, so the form does not change height when
              the payment type is switched. */}
          <Field label={COPY.vendor}>
            <select
              value={vendorId}
              disabled={busy || !isInvoice}
              onChange={(event) => {
                setVendorId(event.target.value);
                setError('');
              }}
              style={{
                ...fieldStyle(busy || !isInvoice),
                opacity: isInvoice ? 1 : 0.5,
                cursor: isInvoice ? 'pointer' : 'not-allowed',
              }}
            >
              <option value="">{vendorsLoading ? COPY.loading : COPY.selectVendor}</option>
              {vendors.map((vendor) => (
                <option key={vendor._id} value={vendor._id}>
                  {vendor.name}
                </option>
              ))}
            </select>
          </Field>

          <div style={{ gridColumn: '1 / -1' }}>
            <Field label={COPY.notes}>
              <input
                value={notes}
                disabled={busy}
                onChange={(event) => setNotes(event.target.value)}
                style={fieldStyle(busy)}
              />
            </Field>
          </div>
        </div>

        <div style={{ paddingTop: 16 }}>
          <ErrorLine>{error}</ErrorLine>
        </div>

        <PrimaryButton
          onClick={submit}
          busy={busy}
          style={{ width: '100%', marginTop: error ? 12 : 20 }}
        >
          {editing ? COPY.saveEdit : COPY.save}
        </PrimaryButton>
      </div>
    </Dialog>
  );
}
